import { z } from "zod";
import type { DatabaseRecord } from "./schema";
import { zWhereabouts } from "./whereabouts";
import { zStoryItem, zStoryItemCue } from "./storyItems";
import { zImageReference } from "./imageReferenceSchema";
import { zStoryFutureIntent } from "./storyTemporalFrames";

/** Narrative order and acceptance revisions are deliberately separate from playback clocks. */
// Saved records tolerate additional metadata (for example imported/created/updated).
// Strip unknown fields while validating every declared field and stable identity.
export const WORLD_SCHEMA_VERSION = 3 as const;
export const zWorldId = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
const ids = z.array(zWorldId).max(2000);
const revision = z.number().int().nonnegative();
export const zWorldVisibility = z.enum(["public", "members", "author"]);
export const zWorldSourceRef = z
  .object({
    accountId: zWorldId,
    storyId: zWorldId,
    branchId: zWorldId.optional(),
    sceneId: zWorldId.optional(),
    beatId: zWorldId.optional(),
    /** Immutable capture ID; ownership and labels never change this identity. */
    sourceId: zWorldId,
    visibility: zWorldVisibility,
  })
  .strip();
export type WorldSourceRef = z.infer<typeof zWorldSourceRef>;
export const zWorldFrontier = z.object({ eventIds: ids }).strip();
export type WorldFrontier = z.infer<typeof zWorldFrontier>;
/** Stable world occurrence; a past frame is distinct from the scene revealing it. */
export const zWorldOccurrenceRef = z.object({ eventId: zWorldId, frameId: zWorldId.optional() }).strip();
export type WorldOccurrenceRef = z.infer<typeof zWorldOccurrenceRef>;
export const zWorldCharacterPosition = z
  .object({
    entityId: zWorldId,
    status: z.enum(["observed", "last-seen", "unknown"]),
    at: z.array(zWorldOccurrenceRef).max(40),
  })
  .strip();
export const zWorldSceneStart = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("now"), entityIds: ids.optional() }).strict(),
  z.object({ mode: z.literal("after"), after: z.array(zWorldOccurrenceRef).max(40) }).strict(),
  z
    .object({
      mode: z.literal("gap"),
      after: z.array(zWorldOccurrenceRef).max(40),
      before: z.array(zWorldOccurrenceRef).min(1).max(40),
    })
    .strict(),
]);
export type WorldSceneStart = z.infer<typeof zWorldSceneStart>;
export const zWorldTimelineBoundary = z
  .object({
    /** Resolved before generation; acceptance does not move this boundary to a new head. */
    revision,
    after: z.array(zWorldOccurrenceRef).max(40),
    before: z.array(zWorldOccurrenceRef).max(40),
    strand: z.object({ id: zWorldId, label: z.string().min(1).max(200) }).optional(),
  })
  .strip();
export const zNarrativeAnchor = z
  .object({
    id: zWorldId,
    description: z.string().max(1000),
    placement: z.enum(["now", "meanwhile", "earlier", "possible-future", "fixed-future"]),
    afterEventIds: ids,
    beforeEventIds: ids,
    overlapEventIds: ids.default([]),
    timeline: zWorldTimelineBoundary.optional(),
  })
  .strip();
export type NarrativeAnchor = z.infer<typeof zNarrativeAnchor>;
export const zWorldWhereabouts = zWhereabouts;
export type WorldWhereabouts = z.infer<typeof zWorldWhereabouts>;
export const zWorldEntity = z
  .object({
    id: zWorldId,
    kind: z.enum(["person", "place", "item"]),
    name: z.string().min(1).max(200),
    description: z.string().max(3000).optional(),
    source: zWorldSourceRef.optional(),
    appearanceRefs: z
      .array(
        z
          .object({
            id: zWorldId,
            storageGeneration: z.string().max(200).optional(),
            image: zImageReference.optional(),
            visibility: zWorldVisibility,
            portable: z.boolean().optional(),
          })
          .strip()
      )
      .max(32)
      .default([]),
    visibility: zWorldVisibility,
  })
  .strip();
export type WorldEntity = z.infer<typeof zWorldEntity>;
const scalar = z.union([z.string().max(6000), z.number().finite(), z.boolean(), z.null()]);
export const zWorldFactValue = z.union([scalar, zWorldWhereabouts, z.record(scalar), z.array(scalar).max(100)]);
export type WorldFactValue = z.infer<typeof zWorldFactValue>;
export const zWorldFact = z
  .object({
    id: zWorldId,
    /** Logical slot; assertion IDs are immutable but a later event can replace its value. */
    key: z.string().min(1).max(500),
    subjectId: zWorldId,
    predicate: z.enum([
      "alive",
      "location",
      "possession",
      "containment",
      "condition",
      "charges",
      "relationship",
      "ability",
      "limit",
      "knowledge",
      "belief",
      "lore",
    ]),
    value: zWorldFactValue,
    certainty: z.enum(["established", "inferred", "unknown"]),
    evidence: z.array(zWorldSourceRef).max(100),
    visibility: zWorldVisibility,
    knownBy: ids.optional(),
    /** The assertion learned here; its occurrence/possibility remains in the cited time frame. */
    learnedFrom: z.object({ source: zWorldSourceRef, frameId: zWorldId, factId: zWorldId }).strip().optional(),
  })
  .strip()
  .superRefine((fact, ctx) => {
    const valid =
      fact.predicate === "alive"
        ? typeof fact.value === "boolean"
        : fact.predicate === "charges"
          ? typeof fact.value === "number" && Number.isInteger(fact.value) && fact.value >= 0
          : fact.predicate === "location"
            ? zWhereabouts.safeParse(fact.value).success
            : fact.predicate === "possession" || fact.predicate === "containment"
              ? fact.value === null || zWorldId.safeParse(fact.value).success
              : fact.predicate === "relationship"
                ? z
                    .union([zWorldId, z.object({ otherId: zWorldId, text: z.string().max(6000) }).strip()])
                    .safeParse(fact.value).success
                : fact.predicate === "condition"
                  ? typeof fact.value === "string"
                  : true;
    if (!valid) ctx.addIssue({ code: "custom", path: ["value"], message: `Invalid ${fact.predicate} fact value.` });
  });
export type WorldFact = z.infer<typeof zWorldFact>;
/** A depicted interval is not a mutation of the recording scene's present state. */
export const zWorldDepiction = z
  .object({
    frameId: zWorldId,
    kind: z.enum(["past", "future", "imagined"]),
    future: zStoryFutureIntent.optional(),
    description: z.string().min(1).max(500),
    source: zWorldSourceRef,
    /** Explicit predecessor in this depicted interval; acceptance order is not story order. */
    previousSource: zWorldSourceRef.optional(),
    participantIds: ids,
    facts: z.array(zWorldFact).max(100),
  })
  .strip()
  .superRefine((frame, ctx) => {
    if ((frame.kind === "future") !== !!frame.future)
      ctx.addIssue({
        code: "custom",
        path: ["future"],
        message: "Future depictions require their basis and certainty.",
      });
  });
export type WorldDepiction = z.infer<typeof zWorldDepiction>;
/** Narrative bookends cite frozen evidence, never playback sequence numbers. */
export const zWorldThreadPosition = z
  .object({
    source: zWorldSourceRef,
    summary: z.string().min(1).max(1000),
  })
  .strip();
export const zWorldThreadContinuation = z
  .object({
    kind: z.enum(["storyline", "gap", "offscreen", "question"]),
    openedAt: zWorldThreadPosition,
    latestAt: zWorldThreadPosition,
    closedAt: zWorldThreadPosition.optional(),
  })
  .strip();
export type WorldThreadContinuation = z.infer<typeof zWorldThreadContinuation>;
export const zWorldThread = z
  .object({
    id: zWorldId,
    title: z.string().min(1).max(300),
    description: z.string().max(3000),
    participantIds: ids,
    status: z.enum(["open", "active", "resolved", "abandoned"]),
    continuation: zWorldThreadContinuation.optional(),
    /** Independent observations can advance a shared goal on parallel strands. */
    progress: z.array(zWorldThreadPosition).max(100).optional(),
    evidence: z.array(zWorldSourceRef).max(100),
    visibility: zWorldVisibility,
  })
  .strip();
export type WorldThread = z.infer<typeof zWorldThread>;
export const zWorldCoverage = z
  .object({
    kind: z.enum(["complete", "scoped"]),
    entityIds: ids,
    factKeys: z.array(z.string().max(500)).max(4000),
    threadIds: ids,
    itemIds: ids,
  })
  .strip();
export const zWorldState = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    worldId: zWorldId,
    revision,
    frontier: zWorldFrontier,
    coverage: zWorldCoverage,
    entities: z.array(zWorldEntity).max(2000),
    facts: z.array(zWorldFact).max(4000),
    threads: z.array(zWorldThread).max(2000),
    items: z.array(zStoryItem).max(200),
  })
  .strip();
export type WorldState = z.infer<typeof zWorldState>;
export const zWorldStyleFields = z
  .object({
    authorDirection: z.string().max(8000).optional(),
    themes: z.array(z.string().max(200)).max(20).optional(),
    culture: z.string().max(4000).optional(),
    storytelling: z
      .object({
        tone: z.string().max(600).optional(),
        pace: z.string().max(300).optional(),
        complexity: z.string().max(300).optional(),
        length: z.enum(["short", "medium", "long"]).optional(),
        narration: z.string().max(600).optional(),
        conflict: z.string().max(1000).optional(),
        endings: z.string().max(1000).optional(),
      })
      .strip()
      .optional(),
    presentation: z
      .object({
        style: z.enum(["story-theatre", "drama", "playful"]).optional(),
        palette: z.array(z.string().max(100)).max(16).optional(),
        artDirection: z.string().max(1500).optional(),
        voiceDirection: z.string().max(1500).optional(),
      })
      .strip()
      .optional(),
    music: z
      .object({
        character: z.string().max(1000).optional(),
        instruments: z.array(z.string().max(100)).max(20).optional(),
        energy: z.string().max(300).optional(),
        ambience: z.string().max(600).optional(),
        underscore: z.string().max(300).optional(),
      })
      .strip()
      .optional(),
    participation: z
      .object({
        mode: z.enum(["listening", "interactive", "mixed"]).optional(),
        invitations: z.string().max(1000).optional(),
        choiceFrequency: z.string().max(300).optional(),
      })
      .strip()
      .optional(),
  })
  .strip();
export type WorldStyleFields = z.infer<typeof zWorldStyleFields>;
/** A request references server-owned state; callers never supply an authoritative continuity context. */
export const zWorldGenerationRef = z
  .object({
    ownerAccountId: zWorldId,
    worldId: zWorldId,
    anchorDescription: z.string().trim().min(1).max(1000).optional(),
    timelineStart: zWorldSceneStart.optional(),
    selectedEntityIds: z.array(zWorldId).max(40).optional(),
    remember: z.boolean().optional(),
    profileOverrides: zWorldStyleFields.optional(),
  })
  .strict();
export type WorldGenerationRef = z.infer<typeof zWorldGenerationRef>;
export const zWorldProfile = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    id: zWorldId,
    worldId: zWorldId,
    revision,
    defaults: zWorldStyleFields,
    reason: z.string().max(1000),
    actorId: zWorldId,
    sourceProfileId: zWorldId.optional(),
  })
  .strip();
export type WorldProfile = z.infer<typeof zWorldProfile>;
export const zResolvedWorldStyle = z
  .object({
    profileRevision: revision,
    fields: zWorldStyleFields,
    provenance: z.record(z.enum(["application", "world", "story", "scene"])),
  })
  .strip();
export type ResolvedWorldStyle = z.infer<typeof zResolvedWorldStyle>;
export const zWorldRecord = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    id: zWorldId,
    ownerAccountId: zWorldId,
    name: z.string().min(1).max(200),
    /** Opt-in additional AI coverage review. Missing means standard, single-pass extraction. */
    enhancedExtraction: z.boolean().optional(),
    /** Complete cover reference, including metadata and thumbnail download URL. */
    image: zImageReference.optional(),
    imageSource: z.literal("first-story").optional(),
    revision,
    profileRevision: revision,
    now: zWorldFrontier,
    isDefault: z.boolean().optional(),
    isDeleted: z.boolean().optional(),
    status: z.enum(["active", "archived"]).optional(),
    visibility: z.enum(["private", "public"]),
    createdBy: zWorldId,
    snapshotId: zWorldId.optional(),
    historyId: zWorldId.optional(),
  })
  .strip();
export type WorldRecord = z.infer<typeof zWorldRecord> & DatabaseRecord;
export const zWorldMember = z
  .object({
    uid: zWorldId,
    accountId: zWorldId.optional(),
    role: z.enum(["viewer", "contributor", "editor", "owner"]),
    entityIds: ids.optional(),
  })
  .strip();
export type WorldMember = z.infer<typeof zWorldMember>;
export const zWorldReadDependency = z
  .object({
    key: z.string().min(1).max(500),
    expected: z.union([
      z.null(),
      z.discriminatedUnion("kind", [
        z
          .object({
            kind: z.literal("fact"),
            subjectId: zWorldId,
            predicate: z.string(),
            value: zWorldFactValue,
            certainty: z.enum(["established", "inferred", "unknown"]),
            knownBy: ids.optional(),
          })
          .strip(),
        z.object({ kind: z.literal("entity"), entityKind: z.enum(["person", "place", "item"]) }).strip(),
        z.object({ kind: z.literal("thread"), status: zWorldThread.shape.status, participantIds: ids }).strip(),
        z
          .object({
            kind: z.literal("item"),
            ownerId: zWorldId.nullable(),
            condition: zStoryItem.shape.condition,
            usesRemaining: z.number().int().nonnegative().nullable(),
            location: zWhereabouts.nullable(),
          })
          .strip(),
      ]),
    ]),
  })
  .strip();
export type WorldReadDependency = z.infer<typeof zWorldReadDependency>;
export const zWorldEffect = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("assert-fact"), fact: zWorldFact }).strip(),
  z.object({ kind: z.literal("retract-fact"), key: z.string().min(1).max(500) }).strip(),
  z.object({ kind: z.literal("upsert-entity"), entity: zWorldEntity }).strip(),
  z.object({ kind: z.literal("upsert-thread"), thread: zWorldThread }).strip(),
  z.object({ kind: z.literal("item"), cue: zStoryItemCue, assertionIds: z.record(zWorldId).optional() }).strip(),
]);
export type WorldEffect = z.infer<typeof zWorldEffect>;
export const zWorldBoundaryConstraint = z
  .object({
    id: zWorldId,
    kind: z.enum(["required-value", "survival", "not-known"]),
    key: z.string().max(500),
    subjectId: zWorldId,
    value: zWorldFactValue,
    beforeEventId: zWorldId.optional(),
    evidence: z.array(zWorldSourceRef).max(100),
    description: z.string().max(1000),
    /** Hidden reasons stay with the checker; the writer gets only the safe requirement. */
    visibility: zWorldVisibility,
  })
  .strip();
export type WorldBoundaryConstraint = z.infer<typeof zWorldBoundaryConstraint>;
export const zWorldChronologyLink = z
  .object({
    before: zWorldOccurrenceRef,
    after: zWorldOccurrenceRef,
  })
  .strip();
export const zWorldEvent = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    id: zWorldId,
    worldId: zWorldId,
    source: zWorldSourceRef,
    description: z.string().max(3000),
    anchor: zNarrativeAnchor,
    participantIds: ids,
    prerequisites: z.array(zWorldReadDependency).max(4000),
    effects: z.array(zWorldEffect).max(1000),
    framing: z.enum(["actual", "imagined", "rehearsal", "possible-future"]),
    depictions: z.array(zWorldDepiction).max(4).optional(),
    /** Accepted ordering evidence; adding links never rewrites the source captures. */
    chronologyLinks: z.array(zWorldChronologyLink).max(100).optional(),
    /** Another presentation of an existing occurrence. Carries no physical or learning effects. */
    portrays: zWorldOccurrenceRef.optional(),
    threadProgress: z
      .array(z.object({ threadId: zWorldId, position: zWorldThreadPosition }).strip())
      .max(100)
      .optional(),
    /** Narrative observations, not stage visibility. An exit requires story evidence. */
    observations: z
      .array(
        z
          .object({
            entityId: zWorldId,
            frameId: zWorldId.optional(),
            beatId: zWorldId.optional(),
            kind: z.enum(["seen", "departed"]),
            whereabouts: zWorldWhereabouts.optional(),
          })
          .strip()
      )
      .max(400)
      .optional(),
  })
  .strip();
export type WorldEvent = z.infer<typeof zWorldEvent>;
export const zWorldProposal = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    id: zWorldId,
    worldId: zWorldId,
    requestId: zWorldId,
    baseRevision: revision,
    readDependencies: z.array(zWorldReadDependency).max(4000),
    events: z.array(zWorldEvent).min(1).max(200),
    boundaryConstraints: z.array(zWorldBoundaryConstraint).max(1000).default([]),
  })
  .strip();
export type WorldProposal = z.infer<typeof zWorldProposal>;
export const zWorldCommit = z
  .object({
    id: zWorldId,
    worldId: zWorldId,
    requestId: zWorldId,
    baseRevision: revision,
    revision,
    eventIds: ids,
    actorId: zWorldId,
    status: z.enum(["remembered", "forgotten"]),
    retractedRevision: revision.optional(),
  })
  .strip();
export type WorldCommit = z.infer<typeof zWorldCommit>;
export const zContinuityDecision = z
  .object({
    id: zWorldId,
    worldId: zWorldId,
    requestId: zWorldId,
    kind: z.enum(["acceptance", "retraction", "placement", "rebase", "repair", "revisit"]),
    baseRevision: revision,
    resultRevision: revision,
    eventIds: ids,
    factKeys: z.array(z.string().max(500)).max(4000),
    rationale: z.string().max(3000),
    selectedResolution: z.string().max(1000),
    alternatives: z.array(z.string().max(1000)).max(20),
    evidence: z.array(zWorldSourceRef).max(1000),
    actorId: zWorldId,
    userOverrode: z.boolean(),
    revisesDecisionId: zWorldId.optional(),
  })
  .strip();
export type ContinuityDecision = z.infer<typeof zContinuityDecision>;
export const zWorldContext = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    worldId: zWorldId,
    revision,
    anchor: zNarrativeAnchor,
    state: zWorldState,
    selectedEntityIds: ids,
    knowledgeEntityIds: ids.optional(),
    depictions: z.array(zWorldDepiction).max(800).optional(),
    chronologyTargets: z
      .array(
        z
          .object({
            eventId: zWorldId,
            source: zWorldSourceRef,
            frameIds: ids,
          })
          .strip()
      )
      .max(2000)
      .optional(),
    localEvents: z.array(zWorldEvent).max(200),
    characterPositions: z.array(zWorldCharacterPosition).max(2000).optional(),
    constraints: z.array(zWorldBoundaryConstraint).max(1000),
    style: zResolvedWorldStyle,
    readDependencies: z.array(zWorldReadDependency).max(4000),
  })
  .strip();
export type WorldContext = z.infer<typeof zWorldContext>;
export const zStoryWorldBinding = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    ownerAccountId: zWorldId,
    worldId: zWorldId,
    startRevision: revision,
    anchor: zNarrativeAnchor,
    entityBindings: z.record(zWorldId),
    remember: z.boolean(),
    profileOverrides: zWorldStyleFields.optional(),
    sourceReleaseId: zWorldId.optional(),
  })
  .strip();
export type StoryWorldBinding = z.infer<typeof zStoryWorldBinding>;
export const zStoryOpportunity = z
  .object({
    path: z
      .object({
        kind: z.enum(["head", "gap", "offscreen"]),
        after: zWorldOccurrenceRef,
        before: zWorldOccurrenceRef.optional(),
      })
      .strip()
      .optional(),
    id: zWorldId,
    worldId: zWorldId,
    title: z.string().max(300),
    invitation: z.string().max(2000),
    kind: z.enum(["start", "continuation", "backstory", "exploration"]),
    anchor: zNarrativeAnchor,
    requiredEntityIds: ids,
    threadIds: ids,
    prerequisites: z.array(zWorldReadDependency).max(4000),
    dependencyKeys: z.array(z.string().max(500)).max(4000),
    readiness: z.enum(["available", "active", "waiting", "resolved", "archived"]),
    checkedRevision: revision,
    evidence: z.array(zWorldSourceRef).max(100),
    curation: z.object({ pinned: z.boolean(), hidden: z.boolean() }).strip(),
    visibility: zWorldVisibility,
    storyId: zWorldId.optional(),
    branchId: zWorldId.optional(),
    narrativePurposes: z.array(z.string().max(200)).max(20).default([]),
    continuation: zWorldThreadContinuation.optional(),
  })
  .strip();
export type StoryOpportunity = z.infer<typeof zStoryOpportunity>;
export const zWorldSeed = z
  .object({
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    id: zWorldId,
    sourceWorldId: zWorldId,
    sourceRevision: revision,
    name: z.string().max(200),
    baseline: zWorldState,
    profile: zWorldProfile,
    assets: z
      .array(
        z
          .object({
            id: zWorldId,
            storageGeneration: z.string().max(200).optional(),
            image: zImageReference,
            portable: z.literal(true),
          })
          .strip()
      )
      .max(2000),
    permissions: z.object({ play: z.boolean(), copy: z.boolean(), adapt: z.boolean(), republish: z.boolean() }).strip(),
    attribution: z.string().max(1000),
    exclusions: z.array(z.string().max(500)).max(2000),
  })
  .strip();
export type WorldSeed = z.infer<typeof zWorldSeed>;
